import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, Loader2, Plus, Search, Users } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { ORIGEM_LABEL, type Clinica, type OrigemRegistro, type SituacaoPaciente } from '@/lib/types'
import { listClinicas } from '@/services/clinicas'
import { listPacientes, type PacienteComClinicas } from '@/services/pacientes'

export default function PacientesPage() {
  const { current } = useAuth()
  const [pacientes, setPacientes] = useState<PacienteComClinicas[]>([])
  const [clinicas, setClinicas] = useState<Clinica[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [busca, setBusca] = useState('')
  const [situacao, setSituacao] = useState<SituacaoPaciente | 'todas'>('ativa')
  const [origem, setOrigem] = useState<OrigemRegistro | 'todas'>('todas')
  const [clinicaId, setClinicaId] = useState<string>('todas')

  const carregar = useCallback(async () => {
    if (!current) return
    setLoading(true)
    setErro(null)
    try {
      const [ps, cs] = await Promise.all([
        listPacientes(current.workspace.id, { busca, situacao, origem, clinicaId }),
        listClinicas(current.workspace.id),
      ])
      setPacientes(ps)
      setClinicas(cs)
    } catch {
      setErro('Não foi possível carregar a carteira.')
    } finally {
      setLoading(false)
    }
  }, [current, busca, situacao, origem, clinicaId])

  useEffect(() => {
    const t = setTimeout(carregar, busca ? 300 : 0)
    return () => clearTimeout(t)
  }, [carregar, busca])

  const nomeClinica = (id: string) => clinicas.find(c => c.id === id)?.nome ?? ''

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Pacientes</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>Sua carteira consolidada, de todas as clínicas.</p>
        </div>
        <Link to="/pacientes/novo" className="btn-primary">
          <Plus size={16} aria-hidden /> Novo paciente
        </Link>
      </header>

      <div className="card" style={{ padding: 12, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} aria-hidden style={{ position: 'absolute', left: 12, top: 14, color: 'var(--muted)' }} />
          <input
            className="field-input" style={{ paddingLeft: 36 }}
            placeholder="Buscar por nome ou CPF"
            value={busca} onChange={e => setBusca(e.target.value)}
            aria-label="Buscar paciente"
          />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select className="field-input" style={{ width: 'auto', minHeight: 38, fontSize: 13.5 }} value={situacao} onChange={e => setSituacao(e.target.value as typeof situacao)} aria-label="Filtrar por situação">
            <option value="ativa">Ativas</option>
            <option value="inativa">Inativas</option>
            <option value="todas">Todas</option>
          </select>
          <select className="field-input" style={{ width: 'auto', minHeight: 38, fontSize: 13.5 }} value={origem} onChange={e => setOrigem(e.target.value as typeof origem)} aria-label="Filtrar por origem">
            <option value="todas">Todas as origens</option>
            {(Object.keys(ORIGEM_LABEL) as OrigemRegistro[]).map(o => (
              <option key={o} value={o}>{ORIGEM_LABEL[o]}</option>
            ))}
          </select>
          <select className="field-input" style={{ width: 'auto', minHeight: 38, fontSize: 13.5 }} value={clinicaId} onChange={e => setClinicaId(e.target.value)} aria-label="Filtrar por clínica">
            <option value="todas">Todas as clínicas</option>
            {clinicas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
      </div>

      {erro && <div className="field-error" role="alert" style={{ marginBottom: 12 }}>{erro}</div>}

      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      ) : pacientes.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <Users size={26} aria-hidden style={{ marginBottom: 8 }} />
          <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Nenhum paciente encontrado</div>
          <div style={{ fontSize: 13.5, marginTop: 4 }}>Cadastre o primeiro paciente da sua carteira.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {pacientes.map(p => (
            <Link key={p.id} to={`/pacientes/${p.id}`} className="card" style={{ padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14.5, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {p.nome}
                  {p.situacao === 'inativa' && <span className="badge badge-muted">Inativa</span>}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span>{ORIGEM_LABEL[p.origem]}</span>
                  {p.clinica_ids.map(id => nomeClinica(id)).filter(Boolean).map(n => <span key={n}>· {n}</span>)}
                </div>
                {p.proxima_acao && (
                  <div style={{ fontSize: 13, color: 'var(--primary-deep)', marginTop: 3, fontWeight: 500 }}>
                    → {p.proxima_acao}
                  </div>
                )}
              </div>
              <ChevronRight size={16} aria-hidden style={{ color: 'var(--muted)', flexShrink: 0 }} />
            </Link>
          ))}
          <div style={{ fontSize: 12.5, color: 'var(--muted)', textAlign: 'center', marginTop: 4 }}>
            {pacientes.length} paciente{pacientes.length === 1 ? '' : 's'}
          </div>
        </div>
      )}
    </div>
  )
}
